// src/pages/PatientsOverview/index.js
import React, { useEffect, useState } from "react";
import { Tabs, Button, Row, Drawer, Grid } from "antd";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchInsuranceList,
  fetchPatientList,
  fetchInsuranceCompaniesLookup, // optional if you want a lookup
} from "./PatientsThunk";
import InsuranceCompanyList from "./components/InsuranceCompanyList";
import PatientsList from "./components/PatientsList";

const { TabPane } = Tabs;

const HELP_CONTENT = {
  patient: {
    title: "Help Manual",
    content: (
      <div style={{ lineHeight: 1.8 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "8px",
          }}
        >
          Add Patient
        </h2>

        <p style={{ color: "#555", marginBottom: "16px", marginLeft: "8px" }}>
          The Add Patient feature allows users to create and manage patient
          records within the ERP system. Accurate patient information helps
          support billing, insurance claims, and reporting.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "14px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>Steps to Add Patient</strong>

          <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
            <li>
              Open <strong>Pharmacy → Patient Overview</strong>
            </li>
            <li>Click the <strong>Patient</strong> tab</li>
            <li>Click <strong>Add Patient</strong></li>
            <li>Enter patient details such as name, code, and contact information</li>
            <li>Link an insurance company if applicable</li>
            <li>Verify all entered information</li>
            <li>Click <strong>Save</strong> to create the patient record</li>
          </ol>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <strong>Important Notes</strong>

          <ul style={{ paddingLeft: "18px", marginTop: "10px", color: "#666" }}>
            <li>Required fields must be completed before saving.</li>
            <li>Patient records can be edited later if needed.</li>
            <li>Each patient can be linked to an insurance company.</li>
          </ul>
        </div>
      </div>
    ),
  },
  insurance: {
    title: "Insurance Company Help",
    content: (
      <div style={{ lineHeight: 1.7 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "8px",
          }}
        >
          Insurance Company
        </h2>

        <p style={{ color: "#555", marginBottom: "16px", marginLeft: "10px" }}>
          Manage the insurance companies used for patient billing and claims
          processing.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>Insurance Company Management</strong>

          <ul style={{ paddingLeft: "18px", marginTop: "8px" }}>
            <li>Create insurance companies</li>
            <li>Edit existing insurance company details</li>
            <li>Delete unused insurance companies</li>
            <li>Link patients to the correct insurance company</li>
          </ul>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "12px",
            borderRadius: "8px",
          }}
        >
          <strong>Benefits</strong>

          <p style={{ marginTop: "8px", color: "#666" }}>
            Maintaining accurate insurance company records simplifies claims
            processing and billing for patient invoices.
          </p>
        </div>
      </div>
    ),
  },
};

const PatientsOverview = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("patient");
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchInsuranceList());
    dispatch(fetchPatientList());
    // optionally populate lookups if you want
    dispatch(fetchInsuranceCompaniesLookup());
  }, [dispatch]);

  return (
    <div style={{ padding: "16px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          data-testid="patients-overview-back-button"
        >
          Back
        </Button>
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        type="card"
      >
        <TabPane tab="Patient" key="patient">
          <PatientsList />
        </TabPane>
        <TabPane tab="Insurance Company" key="insurance">
          <InsuranceCompanyList />
        </TabPane>
      </Tabs>

      <Drawer
        title={HELP_CONTENT[activeTab]?.title}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={400}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        {HELP_CONTENT[activeTab]?.content}
      </Drawer>
    </div>
  );
};

export default PatientsOverview;
